import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Snackbar from "@material-ui/core/Snackbar";
import SearchIcon from "@material-ui/icons/Search";

import utils from "../../universal/utils";
import ServiceTokensSection from "./ServiceTokensSection";

const basePath = utils.getEdgeBasePath();

const ROLE_LABELS = {
  super_admin: "Super Admin",
  review_admin: "Review Admin",
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#1C1E20",
    minHeight: "100vh",
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: theme.spacing(3),
  },
  card: {
    backgroundColor: "#262829",
    borderRadius: 12,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#fff",
    marginBottom: theme.spacing(2),
  },
  sectionText: {
    color: "rgba(255,255,255,0.6)",
    marginBottom: theme.spacing(2),
  },
  searchRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    backgroundColor: "#262829",
    borderRadius: 12,
  },
  tableHead: {
    "& th": {
      fontWeight: 700,
      color: "rgba(255,255,255,0.7)",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
    },
  },
  row: {
    "& td": {
      color: "rgba(255,255,255,0.85)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
      verticalAlign: "top",
    },
  },
  actionRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  metaText: {
    color: "rgba(255,255,255,0.55)",
    fontSize: "0.8rem",
  },
}));

function formatRole(role) {
  return ROLE_LABELS[role] || role || "No access";
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildConfirmMessage(nextRole, targetUserId) {
  if (nextRole === null) {
    return `Remove admin access for ${targetUserId}?`;
  }

  return `Set ${targetUserId} as ${formatRole(nextRole)}?`;
}

export default function AdminAccessPage({ catalog }) {
  const classes = useStyles();
  const user = catalog?.user;

  const [admins, setAdmins] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [snackbar, setSnackbar] = useState("");

  const fetchAccessData = useCallback(
    async (searchValue = activeSearch) => {
      if (!user?.userId) {
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchValue) {
          params.set("search", searchValue);
        }

        const response = await fetch(
          `${basePath}/api/v7/admin/access/adpass/${user.userId}?${params.toString()}`,
          {
            credentials: "include",
          }
        );
        const data = await response.json();

        if (data.ok) {
          setAdmins(data.admins || []);
          setCandidates(data.candidates || []);
        } else {
          setSnackbar(data.error || "Failed to load admin access");
        }
      } catch (error) {
        setSnackbar(error.message || "Failed to load admin access");
      } finally {
        setLoading(false);
      }
    },
    [activeSearch, user?.userId]
  );

  useEffect(() => {
    fetchAccessData("");
  }, [fetchAccessData]);

  const handleSearch = async () => {
    const nextSearch = searchInput.trim();
    setActiveSearch(nextSearch);
    await fetchAccessData(nextSearch);
  };

  const handleRoleUpdate = async ({ targetUserId, adminRole }) => {
    const confirmed = window.confirm(
      buildConfirmMessage(adminRole, targetUserId)
    );
    if (!confirmed) {
      return;
    }

    const nextSavingKey = `${targetUserId}:${adminRole || "remove"}`;
    setSavingKey(nextSavingKey);
    try {
      const response = await fetch(
        `${basePath}/api/v7/admin/access/role/adpass/${user.userId}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            targetUserId,
            adminRole,
          }),
        }
      );
      const data = await response.json();

      if (data.ok) {
        setSnackbar(
          adminRole === null
            ? `Removed admin access from ${targetUserId}`
            : `Updated ${targetUserId} to ${formatRole(adminRole)}`
        );
        await fetchAccessData(activeSearch);
      } else {
        setSnackbar(data.error || "Failed to update admin role");
      }
    } catch (error) {
      setSnackbar(error.message || "Failed to update admin role");
    } finally {
      setSavingKey("");
    }
  };

  const visibleCandidates = useMemo(() => {
    if (!activeSearch) {
      return candidates;
    }

    return candidates;
  }, [activeSearch, candidates]);

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>Admin Access</Typography>

      <Paper className={classes.card}>
        <Typography className={classes.sectionTitle}>
          Manage Admin Roles
        </Typography>
        <Typography className={classes.sectionText}>
          Super admins can promote users to `review_admin` or `super_admin`, or
          remove admin access entirely. The backend prevents removing the last
          super admin.
        </Typography>

        <div className={classes.searchRow}>
          <TextField
            fullWidth
            variant="outlined"
            label="Search by user ID, email, or nickname"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            InputProps={{
              style: { color: "#fff" },
              startAdornment: <SearchIcon style={{ color: "rgba(255,255,255,0.5)", marginRight: 8 }} />,
            }}
            InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
          />
          <Button variant="contained" color="primary" onClick={handleSearch}>
            Search
          </Button>
        </div>
      </Paper>

      <Typography className={classes.sectionTitle}>Current Admins</Typography>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Assigned By</TableCell>
              <TableCell>Assigned At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box py={4}>
                    <CircularProgress style={{ color: "#FF7500" }} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No admins found.
                </TableCell>
              </TableRow>
            ) : (
              admins.map((adminUser) => (
                <TableRow key={adminUser.userId} className={classes.row}>
                  <TableCell>
                    <div>{adminUser.nickname || adminUser.name || adminUser.userId}</div>
                    <div className={classes.metaText}>
                      {adminUser.email || adminUser.userId}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatRole(adminUser.adminRole)}
                      size="small"
                      style={{
                        backgroundColor:
                          adminUser.adminRole === "super_admin"
                            ? "#4caf50"
                            : "#2196f3",
                        color: "#fff",
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>
                  <TableCell>{adminUser.adminRoleAssignedByUserId || "-"}</TableCell>
                  <TableCell>{formatDate(adminUser.adminRoleAssignedAt)}</TableCell>
                  <TableCell>
                    <div className={classes.actionRow}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={
                          savingKey === `${adminUser.userId}:review_admin` ||
                          adminUser.adminRole === "review_admin"
                        }
                        onClick={() =>
                          handleRoleUpdate({
                            targetUserId: adminUser.userId,
                            adminRole: "review_admin",
                          })
                        }
                      >
                        Make Review
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={
                          savingKey === `${adminUser.userId}:super_admin` ||
                          adminUser.adminRole === "super_admin"
                        }
                        onClick={() =>
                          handleRoleUpdate({
                            targetUserId: adminUser.userId,
                            adminRole: "super_admin",
                          })
                        }
                      >
                        Make Super
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        disabled={savingKey === `${adminUser.userId}:remove`}
                        onClick={() =>
                          handleRoleUpdate({
                            targetUserId: adminUser.userId,
                            adminRole: null,
                          })
                        }
                      >
                        Remove Access
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={4}>
        <Typography className={classes.sectionTitle}>Search Results</Typography>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Current Access</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!activeSearch ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Search for a user to grant or change admin access.
                  </TableCell>
                </TableRow>
              ) : visibleCandidates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No matching users found.
                  </TableCell>
                </TableRow>
              ) : (
                visibleCandidates.map((candidate) => (
                  <TableRow key={candidate.userId} className={classes.row}>
                    <TableCell>
                      <div>{candidate.nickname || candidate.name || candidate.userId}</div>
                      <div className={classes.metaText}>
                        {candidate.email || candidate.userId}
                      </div>
                    </TableCell>
                    <TableCell>{candidate.accountStatus || "-"}</TableCell>
                    <TableCell>{formatRole(candidate.adminRole)}</TableCell>
                    <TableCell>
                      <div className={classes.actionRow}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={
                            savingKey === `${candidate.userId}:review_admin` ||
                            candidate.adminRole === "review_admin"
                          }
                          onClick={() =>
                            handleRoleUpdate({
                              targetUserId: candidate.userId,
                              adminRole: "review_admin",
                            })
                          }
                        >
                          Grant Review
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={
                            savingKey === `${candidate.userId}:super_admin` ||
                            candidate.adminRole === "super_admin"
                          }
                          onClick={() =>
                            handleRoleUpdate({
                              targetUserId: candidate.userId,
                              adminRole: "super_admin",
                            })
                          }
                        >
                          Grant Super
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          disabled={savingKey === `${candidate.userId}:remove`}
                          onClick={() =>
                            handleRoleUpdate({
                              targetUserId: candidate.userId,
                              adminRole: null,
                            })
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {user?.userId ? <ServiceTokensSection userId={user.userId} onMessage={setSnackbar} /> : null}

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </div>
  );
}

AdminAccessPage.propTypes = {
  catalog: PropTypes.object.isRequired,
};
